import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppSelector } from "../../Redux/Store/Store";
import { getClient } from "../../Redux/Store/Slices/client";
import Button from "../../Components/Utils/Button/Button";
import ConfirmAction from "../../Components/Utils/ConfirmAction/ConfirmAction";
import FilterSearchBar from "../../Components/SearchBar/FilterSearchBar/FilterSearchBar";
import CompactFilters from "../../Components/Analyse/CompactFilters/CompactFilters";
import type { ISelectOption } from "../../Components/Analyse/CompactFilters/filterTypes";
import {
  createLibraryPortfolio,
  deleteLibraryPortfolio,
  formatBytes,
  getLibraryHome,
  LibraryHome,
  LibraryPortfolioCard,
  patchLibraryPortfolio,
} from "../../API/Library/LibraryCommands";
import { FolderIcon, GridIcon, ListIcon, PlusIcon } from "./LibraryIcons";
import LibraryDialog from "./LibraryDialog";
import LibraryLinkPicker, { linkKey, NO_LINK } from "./LibraryLinkPicker";
import { libraryErrorMessage } from "./libraryUtils";
import styles from "./Library.module.css";

const filterOption = (value: string, label: string): ISelectOption => ({ value, label, required: false });

const accessOptions: ISelectOption[] = [
  filterOption("Private", "Private"),
  filterOption("Public", "Public"),
  filterOption("Global", "Global"),
];

const lockOptions: ISelectOption[] = [
  filterOption("locked", "Locked"),
  filterOption("unlocked", "Unlocked"),
];

const emptyFilters: Record<string, ISelectOption[]> = { access: [], owner: [], lock: [], link: [] };

const VIEW_KEY = "libraryHomeView";

type HomeView = "tiles" | "list";

const readHomeView = (): HomeView => {
  try {
    return window.localStorage.getItem(VIEW_KEY) === "list" ? "list" : "tiles";
  } catch {
    return "tiles";
  }
};

const LibraryHomePage = () => {
  const client = useAppSelector(getClient);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const section = searchParams.get("section") === "archived" ? "archived" : "active";
  const [home, setHome] = useState<LibraryHome | null>(null);
  const [pendingDelete, setPendingDelete] = useState<LibraryPortfolioCard | null>(null);
  const [query, setQuery] = useState("");
  const [selectedFilters, setSelectedFilters] = useState<Record<string, ISelectOption[]>>(emptyFilters);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [link, setLink] = useState("");
  const [view, setView] = useState<HomeView>(readHomeView);
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmArchive, setConfirmArchive] = useState(false);

  const changeView = (next: HomeView) => {
    setView(next);
    try {
      window.localStorage.setItem(VIEW_KEY, next);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (!client?.id) return;
    let active = true;
    getLibraryHome(client.id)
      .then((data) => {
        if (active) setHome(data);
      })
      .catch((caught) => {
        if (active) setError(libraryErrorMessage(caught, "Library could not be loaded."));
      });
    return () => {
      active = false;
    };
  }, [client?.id]);

  useEffect(() => {
    setSelectedIds([]);
  }, [section, client?.id]);

  const listed = useMemo(() => {
    const source = section === "archived" ? home?.archivedPortfolios ?? [] : home?.portfolios ?? [];
    return [...source].sort((a, b) => (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" }));
  }, [home, section]);

  const ownerOptions = useMemo(() => {
    const names: string[] = [];
    for (const card of listed) {
      const name = card.ownerName?.trim() || "No owner listed";
      if (names.indexOf(name) === -1) names.push(name);
    }
    names.sort((a, b) => a.localeCompare(b));
    return names.map((name) => filterOption(name, name));
  }, [listed]);

  const linkOptions = useMemo(() => {
    const names: string[] = [];
    let hasUnlinked = false;
    for (const card of listed) {
      const name = card.linkName?.trim();
      if (!name) {
        hasUnlinked = true;
        continue;
      }
      if (names.indexOf(name) === -1) names.push(name);
    }
    names.sort((a, b) => a.localeCompare(b));
    const options = names.map((name) => filterOption(name, name));
    if (hasUnlinked) options.push(filterOption(NO_LINK, "No link"));
    return options;
  }, [listed]);

  const filterConfig = useMemo(() => [
    { key: "access", label: "Access", placeholder: "Select access", dataSource: "props" as const, data: accessOptions },
    { key: "owner", label: "Owner", placeholder: "Select owners", dataSource: "props" as const, data: ownerOptions },
    { key: "link", label: `${home?.partnerLabel || "Partner"} / department`, placeholder: "Select partner or department", dataSource: "props" as const, data: linkOptions },
    { key: "lock", label: "Lock", placeholder: "Select lock", dataSource: "props" as const, data: lockOptions },
  ], [home?.partnerLabel, ownerOptions, linkOptions]);

  const filtering = !!query.trim() || Object.values(selectedFilters).some((options) => options.length > 0);

  const cards = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const access = (selectedFilters.access ?? []).map((option) => option.value);
    const owners = (selectedFilters.owner ?? []).map((option) => option.value);
    const links = (selectedFilters.link ?? []).map((option) => option.value);
    const locks = (selectedFilters.lock ?? []).map((option) => option.value);
    return listed.filter((card) => {
      if (needle && card.title.toLowerCase().indexOf(needle) === -1) return false;
      if (access.length > 0 && access.indexOf(card.access) === -1) return false;
      if (owners.length > 0 && owners.indexOf(card.ownerName?.trim() || "No owner listed") === -1) return false;
      if (links.length > 0 && links.indexOf(card.linkName?.trim() || NO_LINK) === -1) return false;
      if (locks.length > 0 && locks.indexOf(card.isLocked ? "locked" : "unlocked") === -1) return false;
      return true;
    });
  }, [listed, query, selectedFilters]);

  const linkChoices = home?.linkOptions ?? [];
  const linkRequired = !!home?.linkRequired;
  const chosen = linkChoices.find((option) => linkKey(option) === link);

  const openCreate = () => {
    setTitle("");
    setLink(linkRequired && linkChoices.length > 0 ? linkKey(linkChoices[0]) : NO_LINK);
    setCreating(true);
  };

  const create = async () => {
    if (!client?.id || !title.trim() || (linkRequired && !chosen)) return;
    try {
      const card = await createLibraryPortfolio({
        clientId: client.id,
        title: title.trim(),
        access: "Private",
        linkType: chosen?.type,
        linkId: chosen?.id,
      });
      setCreating(false);
      setTitle("");
      toast.success("Portfolio created");
      navigate(`/library/${card.id}`);
    } catch (caught) {
      toast.error(libraryErrorMessage(caught, "The portfolio could not be created."));
    }
  };

  const reload = async () => {
    if (!client?.id) return;
    setHome(await getLibraryHome(client.id));
  };

  const restore = async (card: LibraryPortfolioCard) => {
    if (!client?.id) return;
    try {
      await patchLibraryPortfolio(card.id, client.id, { isArchived: false });
      toast.success("Portfolio restored");
      await reload();
    } catch (caught) {
      toast.error(libraryErrorMessage(caught, "The portfolio could not be restored."));
    }
  };

  const archiveTargets = section === "active"
    ? cards.filter((card) => card.canDelete && selectedIds.includes(card.id))
    : [];

  const toggleSelected = (id: string) => {
    setSelectedIds((current) => (
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    ));
  };

  const archiveSelected = async () => {
    if (!client?.id || archiveTargets.length === 0) return;
    const failed: string[] = [];
    for (const card of archiveTargets) {
      try {
        await patchLibraryPortfolio(card.id, client.id, { isArchived: true });
      } catch {
        failed.push(card.title);
      }
    }
    setSelectedIds([]);
    await reload();
    if (failed.length > 0) {
      toast.error(
        failed.length === archiveTargets.length
          ? "The portfolios could not be archived."
          : `Could not archive ${failed.join(", ")}.`
      );
      throw new Error("archive failed");
    }
    toast.success(archiveTargets.length === 1 ? "Portfolio archived" : `${archiveTargets.length} portfolios archived`);
  };

  const removeArchived = async () => {
    if (!client?.id || !pendingDelete) return;
    try {
      await deleteLibraryPortfolio(pendingDelete.id, client.id);
      toast.success("Portfolio deleted");
      await reload();
    } catch (caught) {
      toast.error(libraryErrorMessage(caught, "The portfolio could not be deleted."));
      throw caught;
    }
  };

  const used = home?.storeUsedBytes ?? 0;
  const limit = home?.storeLimitBytes ?? 0;
  const usedPercent = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const canCreate = !!home?.canCreate && section === "active";
  const showEmpty = !error && home && listed.length === 0;
  const loading = !home && !error;
  const portfolioCount = listed.length;

  return (
    <div className={styles.page}>
      <div className={styles.homeFrame}>
      <header className={styles.homeHeader}>
        <div className={styles.homeTitleRow}>
          <div className={styles.homeTitleGroup}>
            <h1>Library</h1>
            <div className={styles.sectionToggle} role="tablist" aria-label="Portfolio lists">
              <button
                type="button"
                role="tab"
                aria-selected={section === "active"}
                className={section === "active" ? styles.sectionActive : styles.sectionBtn}
                onClick={() => setSearchParams({}, { replace: true })}
              >
                Portfolios
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={section === "archived"}
                className={section === "archived" ? styles.sectionActive : styles.sectionBtn}
                onClick={() => setSearchParams({ section: "archived" }, { replace: true })}
              >
                Archived
              </button>
            </div>
          </div>
          {home && (
            <span className={styles.homeCount}>
              {portfolioCount} portfolio{portfolioCount === 1 ? "" : "s"}
            </span>
          )}
        </div>
        <div className={styles.homeBar}>
          <div className={styles.homeSearchRow}>
            <div className={styles.homeSearch}>
              <FilterSearchBar
                value={query}
                onChange={setQuery}
                placeholder="Search portfolios"
                filters={filterConfig}
                selectedFilters={selectedFilters}
                onFilterChange={setSelectedFilters}
              />
            </div>
            <div className={styles.viewToggle} role="group" aria-label="Portfolio layout">
              <button
                type="button"
                className={view === "tiles" ? styles.sectionActive : styles.sectionBtn}
                aria-pressed={view === "tiles"}
                title="Tile view"
                onClick={() => changeView("tiles")}
              >
                <GridIcon size={14} />
              </button>
              <button
                type="button"
                className={view === "list" ? styles.sectionActive : styles.sectionBtn}
                aria-pressed={view === "list"}
                title="List view"
                onClick={() => changeView("list")}
              >
                <ListIcon size={14} />
              </button>
            </div>
            <div className={styles.usageCluster}>
              <div
                className={styles.storage}
                role="meter"
                aria-valuemin={0}
                aria-valuemax={limit > 0 ? limit : undefined}
                aria-valuenow={used}
                aria-label={limit > 0 ? `${formatBytes(used)} of ${formatBytes(limit)} used` : `${formatBytes(used)} used`}
              >
                <span className={styles.meterLabel}>
                  {limit > 0 ? `${formatBytes(used)} of ${formatBytes(limit)} used` : `${formatBytes(used)} used`}
                </span>
                {limit > 0 && (
                  <div className={styles.meterTrack} aria-hidden>
                    <div className={styles.meterFill} style={{ width: `${usedPercent}%` }} />
                  </div>
                )}
              </div>
              {canCreate && (
                <Button className={styles.headerButton} clickHandler={openCreate}>
                  <PlusIcon size={14} /> New portfolio
                </Button>
              )}
            </div>
          </div>
          <CompactFilters
            className={styles.homeFilters}
            filters={filterConfig}
            selectedFilters={selectedFilters}
            onFilterChange={setSelectedFilters}
          />
        </div>
      </header>
      <div className={styles.homeBody}>
        {error && <p className={styles.empty}>{error}</p>}
        {loading && (
          <div className={view === "list" ? styles.homeList : styles.grid}>
            {[1, 2, 3, 4, 5, 6].map((card) => (
              <div key={card} className={`${styles.cardSkeleton} ${view === "list" ? styles.cardList : ""}`} aria-hidden>
                <div className={styles.listTitle}>
                  <div className={styles.skeletonIcon} />
                  <div className={styles.skeletonLine} style={{ width: "40%" }} />
                </div>
              </div>
            ))}
          </div>
        )}
        {section === "archived" && !showEmpty && !loading && !error && (
          <p className={styles.sectionHint}>
            Archived portfolios stay here until they are restored or permanently deleted.
          </p>
        )}
        {showEmpty && (
          <div className={styles.emptyState}>
            <img src="/img/Resources/empty.svg" alt="" />
            <p>
              {section === "archived"
                ? "There are no archived portfolios."
                : canCreate
                  ? "Create a portfolio to start storing files."
                  : "There are no portfolios yet."}
            </p>
            {canCreate && <Button clickHandler={openCreate}>New portfolio</Button>}
          </div>
        )}
        {!showEmpty && !error && !loading && cards.length > 0 && (
          <div className={view === "list" ? styles.homeList : styles.grid}>
            {cards.map((card) => (
              <PortfolioCard
                key={card.id}
                card={card}
                layout={view}
                partnerLabel={home?.partnerLabel || "Organisation"}
                selectable={section === "active" && card.canDelete}
                selected={selectedIds.includes(card.id)}
                onToggleSelect={() => toggleSelected(card.id)}
                onOpen={() => navigate(`/library/${card.id}`)}
                onRestore={section === "archived" && card.canDelete ? () => restore(card) : undefined}
                onDelete={section === "archived" && card.canDelete ? () => setPendingDelete(card) : undefined}
              />
            ))}
          </div>
        )}
        {!error && home && cards.length === 0 && filtering && (
          <p className={styles.empty}>No portfolios match that search.</p>
        )}
      </div>
      {archiveTargets.length > 0 && (
        <div className={styles.selectionBar} role="status">
          <span>{archiveTargets.length === 1 ? "1 selected" : `${archiveTargets.length} selected`}</span>
          <Button colour="#fff" textColour="#24394F" size="Small" clickHandler={() => setConfirmArchive(true)}>
            Archive
          </Button>
          <Button colour="#fff" textColour="#24394F" size="Small" clickHandler={() => setSelectedIds([])}>
            Clear
          </Button>
        </div>
      )}
      </div>
      <LibraryDialog isVisible={creating} title="New portfolio" onClose={() => setCreating(false)}>
        <div className={styles.dialogBody}>
          <input value={title} placeholder="Title" onChange={(event) => setTitle(event.target.value)} autoFocus />
          {(linkChoices.length > 0 || !linkRequired) && (
            <LibraryLinkPicker
              options={linkChoices}
              value={link}
              required={linkRequired}
              partnerLabel={home?.partnerLabel || "Partner"}
              onChange={setLink}
            />
          )}
          <div className={styles.dialogActions}>
            <Button colour="#fff" textColour="#24394F" border="#d5dde6" clickHandler={() => setCreating(false)}>Cancel</Button>
            <Button disabled={!title.trim() || (linkRequired && !chosen)} clickHandler={create}>Create</Button>
          </div>
        </div>
      </LibraryDialog>
      <ConfirmAction
        isVisible={confirmArchive}
        onClose={() => setConfirmArchive(false)}
        severity="warning"
        title={archiveTargets.length === 1 ? "Archive portfolio?" : `Archive ${archiveTargets.length} portfolios?`}
        message={archiveTargets.length === 1
          ? `“${archiveTargets[0]?.title ?? "This portfolio"}” will move to Archived portfolios. It can be restored from there, or permanently deleted once it is archived.`
          : "These portfolios will move to Archived portfolios. They can be restored from there, or permanently deleted once they are archived."}
        confirmText="Archive"
        clickHandler={archiveSelected}
      />
      <ConfirmAction
        isVisible={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        severity="danger"
        title="Delete portfolio"
        message={`“${pendingDelete?.title ?? "This portfolio"}” and everything in it will be permanently deleted. This cannot be undone.`}
        confirmText="Delete"
        clickHandler={removeArchived}
      />
    </div>
  );
};

const PortfolioCard = ({
  card,
  layout,
  partnerLabel,
  selectable,
  selected,
  onToggleSelect,
  onOpen,
  onRestore,
  onDelete,
}: {
  card: LibraryPortfolioCard;
  layout: HomeView;
  partnerLabel: string;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
  onOpen: () => void;
  onRestore?: () => void;
  onDelete?: () => void;
}) => {
  const list = layout === "list";
  const cardClass = `${styles.card}${selectable ? ` ${styles.cardSelectable}` : ""}${selected ? ` ${styles.cardSelected}` : ""}`;
  const tick = selectable ? (
    <label
      className={styles.selectWrap}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <input
        type="checkbox"
        className={styles.selectCheckbox}
        checked={!!selected}
        onChange={() => onToggleSelect?.()}
        aria-label={`Select ${card.title}`}
      />
    </label>
  ) : null;
  const pills = (
    <div className={styles.pills}>
      <span className={styles.pill}>{card.access}</span>
      {card.linkName?.trim() && <span className={styles.pill}>{card.linkName}</span>}
      {card.isLocked && (
        <span className={`${styles.pill} ${styles.pillLock}`}>
          <img src="/img/lock-small.svg" alt="" width={10} height={10} /> Locked
        </span>
      )}
    </div>
  );

  if (list) {
    return (
      <div className={`${cardClass} ${styles.cardList}`} onClick={onOpen} role="button" tabIndex={0} onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}>
        {tick}
        <div className={styles.listMain}>
          <div className={styles.listTitle}>
            <FolderIcon size={18} />
            <h2>{card.title}</h2>
          </div>
          <div className={styles.listRight}>
            {pills}
            {(onRestore || onDelete) && (
              <div className={styles.listActions} onClick={(event) => event.stopPropagation()}>
                {onRestore && (
                  <button type="button" className={styles.rowAction} onClick={onRestore}>
                    Restore
                  </button>
                )}
                {onDelete && (
                  <button type="button" className={styles.rowActionDanger} onClick={onDelete}>
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        <div className={styles.listInfo}>
          <p>Access: {card.access}</p>
          <p>Owner: {card.ownerName || "No owner listed"}</p>
          {card.linkName?.trim() && (
            <p>{card.linkType === "Department" ? "Department" : partnerLabel}: {card.linkName}</p>
          )}
          <p>{card.isLocked ? "Locked" : "Unlocked"}</p>
          <p>{card.folderCount} {card.folderCount === 1 ? "folder" : "folders"}</p>
          <p>{card.fileCount} {card.fileCount === 1 ? "file" : "files"}</p>
        </div>
        <div className={styles.listStatus}>
          <span>{card.ownerName || "No owner listed"}</span>
          <span>{card.folderCount} folders · {card.fileCount} files · {formatBytes(card.usedBytes ?? 0)}</span>
        </div>
      </div>
    );
  }

  const body = (
    <>
      <div className={styles.cardTop}>
        <span className={styles.cardMark}><FolderIcon size={20} /></span>
        {pills}
      </div>
      <h2>{card.title}</h2>
      <div className={styles.cardFoot}>
        <span>{card.ownerName || "No owner listed"}</span>
        <span className={styles.cardStats}>
          <span>{card.folderCount} folders · {card.fileCount} files</span>
          <span className={styles.cardUsage}>{formatBytes(card.usedBytes ?? 0)}</span>
        </span>
      </div>
    </>
  );

  if (!onRestore && !onDelete) {
    if (!selectable) {
      return (
        <button type="button" className={styles.card} onClick={onOpen}>
          {body}
        </button>
      );
    }
    return (
      <div className={cardClass}>
        {tick}
        <button type="button" className={styles.cardOpen} onClick={onOpen}>
          {body}
        </button>
      </div>
    );
  }

  return (
    <div className={cardClass}>
      {tick}
      <button type="button" className={styles.cardOpen} onClick={onOpen}>
        {body}
      </button>
      <div className={styles.archivedActions}>
        {onRestore && (
          <button type="button" className={styles.rowAction} onClick={onRestore}>
            Restore
          </button>
        )}
        {onDelete && (
          <button type="button" className={styles.rowActionDanger} onClick={onDelete}>
            Delete
          </button>
        )}
      </div>
    </div>
  );
};

export default LibraryHomePage;
